import { Component, h, Prop, State } from "@stencil/core";

import { AdobeAnalyticsTrackingService } from "../../services/tracking/adobe-analytics-tracking.service";

@Component({
  tag: "scs-flipping-card",
  styleUrls: ["flipping-card.scss"],
  shadow: true,
})
export class FlippingCard {
  private adobeAnalyticsTrackingService: AdobeAnalyticsTrackingService;
  @Prop()
  public preTitle = "";
  @Prop()
  public mainTitle = "";
  @Prop()
  public preTitleColor?: "white" | "navy" = "white";
  @Prop()
  public mainTitleColor?: "white" | "navy" = "white";
  @Prop()
  public mainTitleVisualLevel?:
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "hero"
    | "d1"
    | "d2" = "h2";
  @Prop()
  public imgSrc = "";
  @Prop()
  public imgSrcDesktop = "";
  @Prop()
  public backsideTitle = "";
  @Prop()
  public backsideText = "";
  @Prop()
  public backsideLink = "";
  @Prop()
  public backsideLinkText = "";
  @Prop()
  public textAlign = "";
  @Prop()
  public gradientPosition?: "bottom" | "left" | "top" | "right" | "" = "";
  @Prop()
  public trackingEnabled = false;
  @Prop()
  public trackingAttributes = "{}";
  @State()
  public flipped = false;

  private readonly parsedTrackingAttributes: { [key: string]: string } = {};
  private readonly trackByEvent: (event: Event) => void;

  public constructor() {
    this.adobeAnalyticsTrackingService =
      AdobeAnalyticsTrackingService.getInstance();
    this.trackByEvent = this.adobeAnalyticsTrackingService.trackByEvent.bind(
      this.adobeAnalyticsTrackingService,
    );
    if (this.trackingEnabled) {
      try {
        this.parsedTrackingAttributes = JSON.parse(this.trackingAttributes);
      } catch (error) {
        console.error("Error parsing tracking attributes:", error);
      }
    }
  }

  isDesktop() {
    return window.innerWidth >= 480;
  }

  public render() {
    return (
      <div class="fc-wrapper">
        <div class={{ "fc-card": true, "fc-flipped": this.flipped }}>
          <div
            class={{
              "fc-frontside": true,
              "fc-gradient-position-left": this.gradientPosition === "left",
              "fc-gradient-position-top": this.gradientPosition === "top",
              "fc-gradient-position-right": this.gradientPosition === "right",
            }}
            data-track-action={this.trackingEnabled && "flipping-card-flip"}
            {...this.parsedTrackingAttributes}
            onClick={(ev: Event) => {
              if (this.trackingEnabled) {
                this.trackByEvent(ev);
              }
              this.flipped = true;
            }}
            style={{
              "background-image": `url(${
                this.isDesktop()
                  ? this.imgSrcDesktop || this.imgSrc
                  : this.imgSrc || this.imgSrcDesktop
              })`,
            }}
          >
            <div
              class={{
                "fc-frontside-box": true,
                "fc-frontside-gradient": !!this.gradientPosition,
              }}
            >
              <div
                class={{
                  "fc-frontside-content": true,
                  "fc-align-text-bottom": this.textAlign === "bottom",
                }}
              >
                <h3
                  class={`h4 ${this.preTitleColor === "navy" ? "sc-navy" : ""}`}
                >
                  {this.preTitle}
                </h3>
                <h2
                  class={`${this.mainTitleVisualLevel} ${this.mainTitleColor === "navy" ? "sc-navy" : ""}`}
                >
                  {this.mainTitle}
                </h2>
              </div>
              <sdx-icon
                class="fc-info-icon"
                icon-name="icon-information-circle-filled"
                size={3}
                sr-hint="Info"
              />
            </div>
          </div>
          <div
            class="fc-backside"
            data-track-action={
              this.trackingEnabled && "flipping-card-flip-backwards"
            }
            {...this.parsedTrackingAttributes}
            onClick={(ev: Event) => {
              if (!this.backsideLink) {
                if (this.trackingEnabled) {
                  this.trackByEvent(ev);
                }
                this.flipped = false;
              }
            }}
          >
            <div class="fc-unflip-button-wrapper">
              <sdx-button
                class="fc-unflip-button"
                theme="transparent"
                icon-name="icon-clear-circle"
                icon-size="3"
                label=""
                sr-hint="Flip card back"
                data-track-action={
                  this.trackingEnabled && "flipping-card-flip-backwards"
                }
                {...this.parsedTrackingAttributes}
                onClick={(ev: Event) => {
                  if (this.backsideLink) {
                    if (this.trackingEnabled) {
                      this.trackByEvent(ev);
                    }
                    this.flipped = false;
                  }
                }}
              ></sdx-button>
            </div>
            <div class="fc-backside-container">
              <h3 class="h4">{this.backsideTitle}</h3>
              <p>{this.backsideText}</p>
              {this.backsideLink && (
                <a
                  class="link link--standalone"
                  href={this.backsideLink}
                  {...this.parsedTrackingAttributes}
                  data-track-action={
                    this.trackingEnabled && "flipping-card-link"
                  }
                  onClick={(ev: Event) => {
                    if (this.trackingEnabled) {
                      this.trackByEvent(ev);
                    }
                  }}
                >
                  {this.backsideLinkText || "Show me!"}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
